/**
 * Start (or resume) the attempts for the parts of one paper sitting.
 *
 * `current` is the page's paperId -> attempt_id map. Without `forceNew`, a part
 * already in it is not started again. What is left goes to the server:
 *
 * - two or more ids: one POST /paper_attempts/start_sitting, so every part is
 *   started under one sitting id the server picks. When some parts are
 *   already mapped, one of them goes too, as the anchor: the server reuses
 *   its in-progress attempt (creating nothing) and so sees which sitting the
 *   new parts join. With `forceNew` (a retake) every id is sent and there is
 *   no anchor, so the retake gets a new sitting.
 * - one unmapped id and nothing mapped: one POST /paper_attempts/start, as
 *   before sitting ids existed.
 *
 * If start_sitting fails for any reason (422, 404, 409, 503, the network),
 * the parts are started one by one with /start, exactly as before, so a
 * student's start never breaks on it. Those attempts have no sitting id and
 * the staff view groups them by time.
 *
 * Returns { attempts, sittingId }: `attempts` is a new map holding `current`
 * plus each started part under its requested id and, when the server filed it
 * under another, that id too; `sittingId` is the sitting's id, or null when
 * no sitting was started.
 */
export async function startPaperParts(api, examName, paperIds, current = {}, { forceNew = false } = {}) {
  const ids = [...new Set((paperIds || []).filter(Boolean))];
  const next = { ...current };
  const toStart = forceNew ? ids : ids.filter((paperId) => !next[paperId]);
  if (!toStart.length) return { attempts: next, sittingId: null };

  const anchor = forceNew ? null : ids.find((paperId) => next[paperId]) || null;
  const batch = anchor ? [...toStart, anchor] : toStart;
  let remaining = toStart;
  let sittingId = null;

  if (batch.length >= 2) {
    try {
      const res = await api.startPaperSitting(examName, batch, { forceNew });
      if (!res?.success || !res.attempts) throw new Error('start_sitting returned no attempts');
      toStart.forEach((paperId) => {
        const part = res.attempts[paperId];
        if (!part?.attempt_id) return;
        next[paperId] = part.attempt_id;
        if (part.paper_id && part.paper_id !== paperId) {
          next[part.paper_id] = part.attempt_id;
        }
      });
      sittingId = res.sitting_id || null;
      remaining = toStart.filter((paperId) => !next[paperId]);
      if (remaining.length) console.warn('startPaperSitting left parts unstarted; starting them one by one');
    } catch (err) {
      // No ids or body: the status is enough to tell which refusal it was.
      console.warn('startPaperSitting failed; starting the parts one by one', err?.status ?? null);
    }
  }

  await Promise.all(remaining.map(async (paperId) => {
    try {
      const res = await api.startPaperAttempt(examName, paperId, { forceNew });
      if (res?.success && res.attempt_id) {
        next[paperId] = res.attempt_id;
        if (res.paper_id && res.paper_id !== paperId) {
          next[res.paper_id] = res.attempt_id;
        }
      }
    } catch (err) {
      console.warn('startPaperAttempt failed', paperId, err);
    }
  }));

  return { attempts: next, sittingId };
}

export default startPaperParts;
